import { useState } from 'react';
import { Row, Col, Modal } from 'react-bootstrap';

const Footer = () => {
  const [show, setShow] = useState(false);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');

  const setCookie = () => {
    setTitle('Cookie Policy');
    setMessage(cookieMessage);
    setShow(true);
  }

  const setPrivacy = () => {
    setTitle('Privacy Policy');
    setMessage(privacyMessage);
    setShow(true);
  }

  const setTerms = () => {
    setTitle('Terms & Conditions');
    setMessage(termsMessage);
    setShow(true);
  }

  const cookieMessage = () => {
    return (
      <div>
        <p><strong>Last Updated:</strong> March 16, 2026</p>
        <p>This Cookie Policy explains how OpenSource Cafe ltd ("Company", "we", "us", and "our") uses cookies and similar technologies 
          to recognize you when you visit our website at budget.opensource.cafe ("Website"). It explains what these technologies are and 
          why we use them, as well as your rights to control our use of them.</p>
        <p>
          <strong>1. What are cookies?</strong><br />
          Cookies are small data files that are placed on your computer or mobile device when you visit a website. Cookies are widely 
          used by website owners in order to make their websites work, or to work more efficiently, as well as to provide reporting 
          information.
          Cookies set by the website owner (in this case, OpenSource Cafe ltd) are called "first-party cookies." Cookies set by parties 
          other than the website owner are called "third-party cookies." Third-party cookies enable third-party features or functionality 
          to be provided on or through the website (e.g., advertising, interactive content, and analytics).
         </p>
         <p> 
          <strong>2. Why do we use cookies?</strong><br />
          We use first-party and third-party cookies for several reasons. Some cookies are required for technical reasons in order for 
          our Website to operate, and we refer to these as "essential" or "strictly necessary" cookies. Other cookies also enable us to 
          track and target the interests of our users to enhance the experience on our Online Sections. Third parties serve cookies 
          through our Website for advertising, analytics, and other purposes.
        </p>
        <p>
          <strong>3. Types of Cookies Used</strong><br />
          Strictly Necessary Cookies
          These cookies are essential to provide you with services available through our Website and to use some of its features, such 
          as the budget calculator, debt repayment tools (snowball and avalanche methods), and real-time expense tracking. Because these 
          cookies are strictly necessary to deliver the Website to you, you cannot refuse them without affecting how our Website functions.
          We also use the following essential first-party cookies to support secure authentication after you choose to log in via Google 
          OAuth: ACCESS_TOKEN: set by our backend to authenticate you and help maintain your session securely.
          REFRESH_TOKEN: set by our backend to securely issue new ACCESS_TOKEN cookies when needed, so you do not have to log in 
          again repeatedly.
          Functional Cookies
          These cookies are used to enhance the performance and functionality of our Website but are non-essential to its use. 
          For example, they may remember your preference for a weekly versus a monthly budget view or your manual payment entries 
          within the debt calculator. However, without these cookies, certain functionality may become unavailable.
          Analytics and Customization Cookies
          These cookies collect information that is used either in aggregate form to help us understand how our Website is being 
          used or how effective our budget tools are, or to help us customize our Website for you.
          Third-Party Cookies (Google OAuth Authentication)
          If you use the "Log in with Google" feature, Google will set cookies as part of providing OAuth authentication (we currently 
          use the @react-oauth/google library to enable this). These cookies are used by Google to manage user sessions, security, 
          and authentication. They are strictly necessary for the "Log in with Google" feature to work, and are set and controlled 
          by Google in accordance with Google’s own policies.
          OpenSource Cafe ltd is primarily a client-side app (Redux/React), and we mainly use local storage on your device for app 
          state. As a result, the main third-party cookies you may see in connection with using OpenSource Cafe ltd are those set by 
          Google for authentication.
        </p>
        <p>
          <strong>4. How can I control cookies?</strong><br />
          You have the right to decide whether to accept or reject cookies. You can set or amend your web browser controls to accept 
          or refuse cookies. If you choose to reject cookies, you may still use our website, though your access to some functionality 
          and areas of our website may be restricted.
          As the specific means by which you can refuse cookies through your web browser controls vary from browser to browser, you 
          should visit your browser's help menu for more information.
        </p>
        <p>
          <strong>5. Data Privacy and Open Source Commitment</strong><br />
          OpenSource Cafe ltd is a free, open-source personal finance tool. In alignment with our commitment to transparency and 
          privacy, we do not require sign-ups or downloads. Most data processed by our cookies is stored locally on your device to 
          ensure that your financial information remains private and is not stored in corporate systems.
        </p>
        <p>
          <strong>6. Updates to this Cookie Policy</strong><br />
          We may update this Cookie Policy from time to time in order to reflect, for example, changes to the cookies we use or for 
          other operational, legal, or regulatory reasons. Please therefore re-visit this Cookie Policy regularly to stay informed 
          about our use of cookies and related technologies.
        </p>
        <p> 
          <strong>7. Contact Us</strong><br />
          If you have any questions about our use of cookies or other technologies, please contact us through the communication channels 
          provided at <a href="https://www.opensource.cafe">www.opensource.cafe</a>
        </p>
      </div>
    );
  }

  const privacyMessage = () => {
    return (
      <>
      <header>
        <p><strong>Last Updated:</strong> 19 March 2026</p>
      </header>

        <main>
          <section aria-labelledby="intro">
            <strong>1. Introduction</strong>
            <p>
              At&nbsp;<strong>OpenSource Cafe ltd</strong>, we run the <strong>Budget</strong> app at
              <strong>budget.opensource.cafe</strong>. We believe managing your money should be simple,
              transparent, and private. We try to be equally clear about how we handle personal data.
            </p>
            <p>This Privacy Policy explains what data we collect, why we collect it, and how we protect it.</p>
          </section>

          <section aria-labelledby="data-collect">
            <strong>2. Data We Collect</strong>
            <p>We aim to collect as little personal data as possible, and we don’t ask for more than we need.</p>
            <ul>
              <li>
                <strong>Google OAuth login data:</strong> If you choose to sign in with <strong>Google OAuth</strong>, we receive
                basic account information from Google such as your <strong>email address</strong> and <strong>profile name</strong>
                (and a unique identifier used to link your login).
              </li>
              <li>
                <strong>Financial data you enter into Budget:</strong> Budget is designed to be <strong>client-side first</strong>,
                so most of the financial information you enter (like income, expenses, and debt details) is stored on your device
                (see “How We Store Data” below) rather than being sent to us by default.
              </li>
              <li>
                <strong>Technical/session data:</strong> We use essential authentication cookies/tokens to manage your session and
                keep your account secure (see “How We Store Data” below).
              </li>
            </ul>
          </section>

          <section aria-labelledby="how-use">
            <strong>2. How We Use Your Information</strong>
            <p>We use your data strictly to provide and improve our services:</p>
            <ul>
              <li>To calculate your remaining budget and debt repayment timelines.</li>
              <li>To allow you to access your data across different devices (if using Google Login).</li>
              <li>To ensure the security and stability of our website.</li>
            </ul>
            <p><strong>We do not sell your personal or financial data to third parties, nor do we use it for advertising.</strong></p>
          </section>

          <section aria-labelledby="how-store">
            <strong>3. How We Store Data</strong>
            <p>Budget is a <strong>client-side first</strong> app.</p>
            <ul>
              <li>
                <strong>Local storage (on your device):</strong> Most of your financial data (including income, expenses, and debts)
                is stored in your browser’s local storage. This generally means it stays on your device and is not automatically
                uploaded to our servers.
              </li>
              <li>
                <strong>Authentication cookies (backend):</strong> Our backend handles authentication using cookies called
                <code>ACCESS_TOKEN</code> and <code>REFRESH_TOKEN</code>. These are used to keep you logged in and help protect your 
                account.
              </li>
            </ul>
          </section>

          <section aria-labelledby="third-party">
            <strong>4. Third-Party Services</strong>
            <p>
              We use <strong>Google</strong> for authentication (Google OAuth). If you use Google login, you should review Google’s
              privacy policy to understand how they process your information.
            </p>
            <p><strong>We don’t sell your data</strong> and we don’t use it for advertising.</p>
          </section>

          <section>
            <strong>5. Your Rights</strong>
            <p>
              Under <strong>UK GDPR</strong> (and the <strong>Data (Use and Access) Act 2025</strong>), you have rights in relation
              to your personal data, including:
            </p>
            <ul>
              <li><strong>Access:</strong> ask for a copy of the personal data we hold about you.</li>
              <li><strong>Delete:</strong> ask us to delete personal data we hold about you (where the law allows).</li>
              <li><strong>Portability:</strong> ask for your personal data in a portable, machine-readable format (where applicable).</li>
              <li><strong>Correction:</strong> ask us to fix inaccurate or incomplete personal data.</li>
            </ul>
            <p>To exercise any of these rights, please contact us using the details below.</p>
          </section>

          <section aria-labelledby="changes-1">
            <strong>7. Changes to This Policy</strong>
            <p>
              We may update this policy from time to time to reflect changes in our software or legal requirements. We will notify users
              of any significant changes by updating the "Last Updated" date at the top of this page.
            </p>
          </section>

          <section aria-labelledby="contact">
            <strong id="contact">8. Contact Us</strong>
            <p>
              If you have any questions about this Privacy Policy or how we handle your data, please visit 
              <strong>budget.opensource.cafe</strong> for more info or contact:
            </p>
            <address>
              <strong>OpenSource Cafe ltd</strong><br />
              Email: <a href="mailto:craig@opensource.cafe">craig@opensource.cafe</a><br />
              Website: <a href="https://www.opensource.cafe">www.opensource.cafe</a>
            </address>
          </section>
        </main>
      </>
    )
  }

  const termsMessage = () => {
    return (
      <>
        <header>
          <p><strong>Last Updated:</strong> 19 March 2026</p>
        </header>

        <main>
          <section aria-labelledby="intro">
            <strong>1. Introduction &amp; acceptance</strong>
            <p>
              These Terms and Conditions ("Terms") govern your use of the Budget personal finance tool and the website
              located at <a href="https://budget.opensource.cafe">budget.opensource.cafe</a> (the "Service"), operated by
              OpenSource Cafe ltd ("Company", "we", "us", or "our").
            </p>
            <p>
              By accessing or using the Service, you agree to these Terms. If you don’t agree, please stop using the Service.
            </p>
          </section>

          <section aria-labelledby="personal-use">
            <strong>2. Personal use (what Budget is)</strong>
            <p>
              Budget is a free, open-source budget calculator designed for personal finance tracking. It helps you:
            </p>
            <ul>
              <li>Track income and expenses on monthly and weekly bases</li>
              <li>Plan debt repayment using snowball and avalanche methods</li>
              <li>See your remaining budget in real time</li>
            </ul>
            <p>
              The Service is provided free of charge. No download is required for normal browser-based use.
            </p>
          </section>

          <section aria-labelledby="no-advice">
            <strong>3. No professional advice</strong>
            <p>
              Budget provides general information and automated calculations for educational/illustrative purposes only.
              We’re not a bank, a financial advisor, a debt counsellor, or a regulated financial institution.
            </p>
            <ul>
              <li>Nothing on the Service is financial, legal, tax, or other professional advice.</li>
              <li>The results depend on the information you enter and may not reflect your full situation.</li>
              <li>You should speak to a qualified professional before making major financial decisions.</li>
            </ul>
          </section>

          <section aria-labelledby="privacy">
            <strong>4. Data privacy &amp; login</strong>
            <p>
              Budget is built with privacy in mind. In general, your budgeting data is stored locally on your device/browser
              (client-side). You’re responsible for securing your device and keeping backups of anything you don’t want to lose.
            </p>
            <p>
              If you choose to sign in, we use Google OAuth to authenticate you. Please read our Privacy Policy for details on
              what we collect (if anything), how it’s used, and how client-side storage works.
            </p>
            <p>
              <span onClick={() => setPrivacy()}>Privacy Policy</span>
            </p>
          </section>

          <section aria-labelledby="open-source">
            <strong>5. Open source</strong>
            <p>
              The “Budget” name, “OpenSource Cafe ltd” brand, and any logos or proprietary design elements remain ours and can’t be
              used without our written permission, unless the law allows it.
            </p>
          </section>

          <section aria-labelledby="disclaimer">
            <strong>6. Disclaimer</strong>
            <p>
              The Service is provided on an “as is” and “as available” basis. To the maximum extent permitted by law, we disclaim all
              warranties (express or implied), including merchantability, fitness for a particular purpose, and non-infringement.
            </p>
            <p>
              We don’t promise that the Service will be uninterrupted, secure, error-free, or that calculations will be accurate for
              your needs.
            </p>
          </section>

          <section aria-labelledby="liability">
            <strong>7. Liability (limits)</strong>
            <p>
              To the maximum extent permitted by applicable law, OpenSource Cafe ltd (and its directors, employees, and contributors)
              won’t be liable for any losses or damages arising out of or connected with your use of the Service.
            </p>
            <p>
              This includes (without limitation) financial losses, loss of savings, lost profits, loss of data, incorrect outputs,
              or issues caused by third-party services (including Google OAuth).
            </p>
            <p>
              Nothing in these Terms limits or excludes liability where it would be unlawful to do so (for example, for fraud).
            </p>
          </section>

          <section aria-labelledby="governing-law">
            <strong>8. Governing law</strong>
            <p>
              These Terms are governed by the laws of England and Wales. Any dispute arising out of or in connection with these Terms
              will be subject to the exclusive jurisdiction of the courts of England and Wales.
            </p>
          </section>

          <section aria-labelledby="changes">
            <strong>9. Changes to the Service or these Terms</strong>
            <p>
              We may update or change the Service (or any part of it) at any time. We may also update these Terms from time to time.
              The latest version will be posted on our website, and your continued use of the Service means you accept the updated Terms.
            </p>
          </section>

          <section aria-labelledby="contact">
            <strong>10. Contact</strong>
            <p>
              Questions about these Terms? Contact us via the official channels listed at&nbsp;
              <a href="https://www.opensource.cafe">www.opensource.cafe</a>.
            </p>
          </section>
        </main>
      </>
    )
  }

  return(
    <Row className="footer-row justify-content-md-center">
      <p className="footer text-md-center">Copyright OpenSource.cafe Ltd {new Date().getFullYear()}</p>
      <Row className="justify-content-md-center">
        <Col xs={12} md={1}>
          <p className="policy-links text-md-center"><span onClick={() => setCookie()}>Cookie Policy</span></p>
        </Col>
        <Col xs={12} md={1}>
          <p className="policy-links text-md-center"><span onClick={() => setPrivacy()}>Privacy Policy</span></p>
        </Col>
        <Col xs={12} md={1}>
          <p className="policy-links text-md-center"><span onClick={() => setTerms()}>Terms & Conditions</span></p>
        </Col>
      </Row>
      <Row className="justify-content-md-center">
        <Col xs={12} md={6}>
          <p onClick={() => setCookie()} className="policy-links text-md-center">We only use essential cookies to keep you logged in. 
            No tracking, no ads. Check our Cookie Policy if you're curious.</p>
        </Col>
        <Modal
          size="lg"
          show={show}
          onHide={() => setShow(false)}
          aria-labelledby="policy-modal-lg"
        >
          <Modal.Header closeButton>
            <Modal.Title id="policy-modal-lg">
              {title}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {message}
          </Modal.Body>
        </Modal>
      </Row>
    </Row>
  )
};

export default Footer;